import { Stack } from '@mui/material';
import TextField from '@/components/ui/form-inputs/TextField';
import { USER_FORM_KEY } from '@/shared/constants/form-keys';
import { ChangeEvent, Fragment, ReactNode, useMemo } from 'react';
import SelectField from '@/components/ui/form-inputs/SelectField';
import { Role } from '@/core/entities';
import { CustomerSiteOption } from '@/api/customers/types';
import { Roles } from '@/core/types';
import SiteSelectField from './site-select/SiteSelectField';
import { UserFormModel } from './types';
import { SiteSelectType } from './site-select/types';

interface UserFormFieldsProps {
	formModel: UserFormModel;
	updateFormModel: (value: UserFormModel) => void;
	isAdminForm?: boolean;
	isEmailDisabled?: boolean;
	roleOptions?: Role[];
	siteField?: {
		type: SiteSelectType;
		siteOptions?: CustomerSiteOption[];
	};
	pin?: string;
	isOperator: boolean;
	guidance?: ReactNode;
}

const UserFormFields = ({
	formModel,
	updateFormModel,
	isAdminForm,
	isEmailDisabled,
	roleOptions,
	siteField,
	pin,
	isOperator,
	guidance,
}: UserFormFieldsProps) => {
	const { siteOptions, type: siteFieldType } = siteField || {};

	const isAccountAdminOption = (roleId: string) =>
		roleId === roleOptions?.find((role) => role.title === Roles.ACCOUNT_ADMIN)?.id;

	const accountAdminRoleSelected = useMemo(
		() => isAccountAdminOption(formModel.roleId),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[formModel.roleId, roleOptions]
	);

	const onRoleUpdate = (event: ChangeEvent<HTMLInputElement>) => {
		const sites = isAccountAdminOption(event.target.value)
			? siteOptions?.map((option) => option.id)
			: formModel.sites;

		updateFormModel({
			...formModel,
			roleId: event.target.value,
			sites,
		});
	};

	return (
		<Stack direction="column" spacing={2}>
			<TextField
				id="firstName"
				name="firstName"
				label="First name"
				formKey={USER_FORM_KEY}
				required
				fullWidth
				value={formModel.firstName}
				onChange={(event: ChangeEvent<HTMLInputElement>) =>
					updateFormModel({ ...formModel, firstName: event.target.value })
				}
			/>
			<TextField
				id="lastName"
				name="lastName"
				label="Last name"
				formKey={USER_FORM_KEY}
				required
				fullWidth
				value={formModel.lastName}
				onChange={(event: ChangeEvent<HTMLInputElement>) =>
					updateFormModel({ ...formModel, lastName: event.target.value })
				}
			/>
			<TextField
				id="email"
				name="email"
				label="Email address"
				type="email"
				formKey={USER_FORM_KEY}
				disabled={isEmailDisabled}
				fullWidth
				value={formModel?.email ?? ''}
				onChange={(event: ChangeEvent<HTMLInputElement>) =>
					updateFormModel({ ...formModel, email: event.target.value })
				}
			/>
			{!isAdminForm && (
				<Fragment>
					<SelectField
						id="role"
						name="role"
						label="Role"
						formKey={USER_FORM_KEY}
						required
						fullWidth
						value={formModel?.roleId ?? ''}
						onChange={onRoleUpdate}
						options={roleOptions?.map((role) => ({ value: role.id, label: role.title }))}
						disabled={isOperator}
					/>
					<SiteSelectField
						type={siteFieldType || SiteSelectType.MULTISELECT}
						formModel={formModel}
						updateFormModel={updateFormModel}
						sites={siteOptions}
						disabled={accountAdminRoleSelected}
					/>
				</Fragment>
			)}
			{pin && (
				<TextField
					id="pin"
					name="pin"
					label="PIN"
					formKey={USER_FORM_KEY}
					disabled
					fullWidth
					value={pin}
				/>
			)}
			{guidance}
		</Stack>
	);
};

export default UserFormFields;
